import { useAtom, useSetAtom } from "jotai";
import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Page, PageStatus, Planogram } from "src/components";
import { useLoaders } from "src/components/InlineLoader";
import { Button, Color, Flex, Icon, Text } from "src/elements";
import { useModifiedPog } from "src/hooks/useModifiedPogAtom";
import { useUnsavedChangesGuard } from "src/hooks/useUnsavedChangesGuard";
import { useTranslation } from "src/i18n";
import {
  ResponseGetMerchflowBasePog_basePogId_,
  RoutePostMerchflowBasePog_basePogId_,
} from "@CommonApi/merchflow/base-pog/_basePogId_";
import { highlightsModeAtom } from "src/components/Planogram/store/atoms";
import {
  planogramSidepanelIsExpandedAtom,
  usePlanogramPanel,
} from "src/components/Planogram/components/Panel/store/hooks";
import { STUDIO_WIZARD_PARAM } from "src/pages/CdtStudio/store/atoms";
import { PogAtom } from "src/components/Planogram/store/types";
import { PlanogramMetrics } from "src/components/PlanogramExtensions/PlanogramMetrics";
import { useStorePlanogramRulesModal } from "src/modals/StorePlanogramRules/store/hooks";
import { pages } from "src/utils/pages";
import { useNavigation } from "src/utils/navigation";
import { MerchflowHeader } from "../../components/merchflows/MerchflowHeader/MerchflowHeader";
import { MerchflowStepsNavigator } from "../../components/merchflows/MerchflowStepsNavigator/MerchflowStepsNavigator";
import {
  merchflows_merchflowId_BasePog_basePogId_Atom,
  merchflows_merchflowId_BasePog_basePogId_globalChangesAtom,
  merchflows_merchflowId_BasePog_basePogId_PogAtom,
} from "./store/atoms";
import { PlanogramHovererDetails } from "src/components/PlanogramExtensions/PlanogramHovererDetails/PlanogramHovererDetails";
import { usePowerBiReportModal } from "src/modals/PowerBiReportModal/hooks";
import { RoutePostMerchflowApplyToBases } from "@CommonApi/merchflow/apply-to-bases";
import { useApi } from "src/api";
import { RoutePutBasePogSelectedStatus } from "@CommonApi/pogger/base-pog/status";
import { RouteGetMerchflows_merchflowId_BasePog_basePogId_ } from "@CommonApi/merchflows/_merchflowId_/base-pog/_basePogId_";
import { sleep } from "src/utils/sleep";
import { RouteGetMerchflows_merchflowId_StoresAll } from "@CommonApi/merchflows/_merchflowId_/stores/all";
import { PlanogramLegend } from "src/components/PlanogramExtensions/PlanogramLegend";
import { RoutePostMerchflowApplyToBasesByCopyStores } from "@CommonApi/merchflow/apply-to-bases-by-copy-stores";
import { downloadFile, downloadFileFromUrl } from "src/utils/downloadFile";
import psaAdapter from "src/omni-common/utils/psa/export/PsaAdapter";
import { RouteGetMerchflowBasePog_basePogId_logs } from "@CommonApi/merchflow/base-pog/_basePogId_/logs";
import { routeToUrl } from "@CommonUtils/routes";
import get from "es-toolkit/compat/get";
import { ResponseMerchFlowApplyFilters } from "@CommonApi/merchandise";
import { PlanogramToolbar } from "src/components/Planogram/components/Toolbar/PlanogramToolbar";
import { useModals } from "src/components/Modals";
import { hasPermission, PermissionCode } from "@CommonUtils/RolesPermissions";
import { useUser } from "src/atoms/user";

const extractBasePogError = (data: ResponseGetMerchflowBasePog_basePogId_) => {
  let errorMsg = "Base planogram generation failed";

  for (const error of Object.keys(data.error_logs)) {
    if ("detail" in get(data.error_logs, error, {})) {
      errorMsg = get(data.error_logs, `${error}.detail`, errorMsg);
      break;
    }
  }

  return errorMsg;
};

const StoreProductGenerationInfoState = () => (
  <Flex
    flexGrow
    minHeight="320px"
    background={Color.lightGray}
    borderColor={Color.lightGrayHover}
    borderRadius="3px"
    padding="32px"
    column
    align="center"
    justify="center"
    gap="12px"
  >
    <Icon name="info" size={40} color={Color.primary} />

    <Text variant="h3" textAlign="center">
      No Base POG uploaded
    </Text>

    <Text variant="body3" color={Color.textSecondary} textAlign="center">
      Store planograms will be generated using the current CDT and priorities from each selected
      store.
    </Text>
  </Flex>
);

export const PageMerchflows_merchflowId_BasePog_basePogId_ = () => {
  const {
    getMerchflowBasePogByMerchflowIdAndBasePogIdApi,
    putMerchflowBasePogApi,
    postMerchflowBasePog_basePogId_StatusApi,
    getMerchflowBasePogLogsApi,
    getMerchflowReviewApi,
  } = useApi();
  const { hasLoader } = useLoaders();
  const { navigate } = useNavigation();
  const { openStorePlanogramRules } = useStorePlanogramRulesModal();

  const openGenerateStorePlanograms = async () => {
    openStorePlanogramRules({
      mode: "store",
      merchflowId,
      basePogId,
      isBasePogFavorited: !!data?.favorited,
      storeCodes: availableStores?.stores?.map((store) => store.store) || [],
      defaultHighDensityEnabled: data?.flow_review?.config?.flowType === "UHD" ? true : undefined,
    });
  };
  const { openBasePogReport } = usePowerBiReportModal();
  const { openConfirmModal } = useModals();
  const { t } = useTranslation("common");
  const { user } = useUser();
  const canCreate = hasPermission(user.roleCode, PermissionCode.MERCHFLOW_CREATE);

  const { merchflowId: _merchflowId, basePogId: _basePogId } = useParams<{
    merchflowId: string;
    basePogId: string;
  }>();
  const merchflowId = Number(_merchflowId);
  const basePogId = Number(_basePogId);

  const [searchParams] = useSearchParams();
  const isStudioTab = searchParams.get(STUDIO_WIZARD_PARAM) === "1";
  const { setTab: setPanelTab } = usePlanogramPanel();
  const setPanelExpanded = useSetAtom(planogramSidepanelIsExpandedAtom);
  // setTab's identity changes every render; putting it in deps loops open/close.
  const setPanelTabRef = useRef(setPanelTab);
  setPanelTabRef.current = setPanelTab;
  useEffect(() => {
    if (isStudioTab) {
      setPanelExpanded(true);
      setPanelTabRef.current("CDT_STUDIO", true); // lock from a prior session must not block the wizard
    }
  }, [isStudioTab, setPanelExpanded]);
  const [data, setData] = useAtom(merchflows_merchflowId_BasePog_basePogId_Atom);
  const setHighlightsMode = useSetAtom(highlightsModeAtom);
  const [globalChanges, setGlobalChanges] = useAtom(
    merchflows_merchflowId_BasePog_basePogId_globalChangesAtom,
  );

  const [isFavoriteDone, setIsFavoriteDone] = useState(false);
  const [isSavingDone, setIsSavingDone] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [availableStores, setAvailableStores] = useState<
    ResponseMerchFlowApplyFilters["filter_config"] | null
  >(null);

  const {
    isModified,
    setPog: setBasePog,
    pog: basePog,
    revertPog,
    savePogAsOriginal,
  } = useModifiedPog(merchflows_merchflowId_BasePog_basePogId_PogAtom);

  useUnsavedChangesGuard(isModified, {
    onSave: () => save(false, false),
    saveActions: globalChanges
      ? [
          {
            id: "save-only",
            label: t("productsCdtsChanged.saveOnly"),
            color: "greenSmoke",
            variant: "inverted",
            onSave: () => save(false, false),
          },
          {
            id: "save-and-update-products",
            label: t("productsCdtsChanged.saveAndUpdateProducts"),
            onSave: () => save(false, true),
          },
        ]
      : undefined,
  });

  const isPogAvailable = Boolean(data && data?.pog !== null);
  const isPlanogramLoading =
    data === undefined ||
    (data?.status !== "IN_PROGRESS" &&
      hasLoader(RouteGetMerchflows_merchflowId_BasePog_basePogId_));
  const isSaveLoading = hasLoader(RoutePostMerchflowBasePog_basePogId_);
  const isFavoriteLoading = hasLoader(RoutePutBasePogSelectedStatus);
  const isGenerateStorePlanogramsLoading =
    hasLoader(RoutePostMerchflowApplyToBases) ||
    hasLoader(RouteGetMerchflows_merchflowId_StoresAll) ||
    hasLoader(RoutePostMerchflowApplyToBasesByCopyStores);
  const isStoreProductMode = data?.generationCdtSource === "STORE_PRODUCT";
  const isGenerateStorePlanogramsEnabled = isPogAvailable || isStoreProductMode;

  useEffect(() => {
    if (merchflowId) {
      void loadStores(merchflowId);
    }
  }, [merchflowId]);

  const loadStores = async (merchflowId: number) => {
    const response = await getMerchflowReviewApi(merchflowId);
    if (response) {
      setAvailableStores(response.filter_config);
    }
  };

  useEffect(() => {
    setData(undefined);
    setBasePog(null);
    setHighlightsMode(false);
    loadBasePog();

    return () => {
      setData(undefined);
      setBasePog(null);
      setGlobalChanges(false);
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [merchflowId, basePogId]);

  const loadBasePog = async () => {
    const response = await getMerchflowBasePogByMerchflowIdAndBasePogIdApi(merchflowId, basePogId);
    setData(response);

    if (response?.status === "IN_PROGRESS") {
      await sleep(10000);
      loadBasePog();
    }
  };

  useEffect(() => {
    if (isStoreProductMode) return;

    if (data?.pog && basePog === null) {
      setBasePog(data.pog);
    }
  }, [data]);

  const onFavorite = async () => {
    if (!data) return;

    if (
      await postMerchflowBasePog_basePogId_StatusApi({
        is_not_favourite: data.favorited ? [basePogId] : [],
        is_favourite: !data.favorited ? [basePogId] : [],
      })
    ) {
      setIsFavoriteDone(true);
      setTimeout(() => {
        setData({ ...data, favorited: !data.favorited });
        setIsFavoriteDone(false);
      }, 1000);
    }
  };

  const onClickSave = async (saveAsNew: boolean) => {
    if (globalChanges) {
      openConfirmModal({
        title: t("productsCdtsChanged.title"),
        description: t("productsCdtsChanged.description"),
        buttons: [
          {
            id: "cancel",
            label: "Cancel",
            color: "yellow",
            variant: "inverted",
          },
          {
            id: "save-only",
            label: t("productsCdtsChanged.saveOnly"),
            color: "greenSmoke",
            onClick: () => {
              save(saveAsNew, false);
            },
          },
          {
            id: "save-and-update-products",
            label: t("productsCdtsChanged.saveAndUpdateProducts"),
            onClick: () => {
              save(saveAsNew, true);
            },
          },
        ],
      });
    } else {
      save(saveAsNew, false);
    }
  };

  const save = async (saveAsNew: boolean, saveGlobally: boolean): Promise<boolean> => {
    if (!basePog) return false;

    const pogToSave = basePog.toNonCircular();
    const newBasePogId = await putMerchflowBasePogApi(basePogId, {
      pog: pogToSave,
      saveAsNew,
      saveProductsCdtsGlobally: saveGlobally,
    });

    if (!newBasePogId) return false;

    savePogAsOriginal(pogToSave);
    setGlobalChanges(false);
    setIsSavingDone(true);
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      setIsSavingDone(false);

      if (saveAsNew && newBasePogId !== basePogId) {
        navigate(
          routeToUrl(pages.merchflows_merchflowId_BasePog_basePogId_, {
            merchflowId,
            basePogId: newBasePogId,
          }),
        );
      }
    }, 1000);

    return true;
  };

  const openReport = () => {
    if (!data) return;

    openBasePogReport([
      {
        column: "filter_config_id",
        table: "gen_id_table",
        value: [data.filter_config_id],
      },
      {
        column: "id",
        table: "base_id",
        value: [data.id],
      },
    ]);
  };

  const downloadLogs = async (
    logsType: "pm_data_path" | "pogger_request_path" | "pogger_response_path",
  ) => {
    const response = await getMerchflowBasePogLogsApi(basePogId);

    if (response && response[logsType]) {
      await downloadFileFromUrl(response[logsType]);
    }
  };

  const downloadModifiedPsa = () => {
    if (!basePog) return;
    // Import reads store from the Planogram name (bare_store_code).
    const { psaString } = psaAdapter.convertToPsa(basePog, {
      planogramName: data?.store_code || undefined,
    });
    downloadFile({
      name: `base-pog-${basePogId}-modified`,
      extension: "psa",
      data: psaString,
    });
  };

  return (
    <Page>
      <MerchflowHeader
        merchflowId={merchflowId}
        templateId={data?.template_id || null}
        basePogId={basePogId}
      />

      <MerchflowStepsNavigator
        activeStep={isStudioTab ? "CDT_STUDIO" : "BASE_POG"}
        merchflowId={merchflowId}
        basePogId={basePogId}
        s2SolutionId={data?.s2_solution_id || undefined}
      />

      <Flex gap="10px" flexGrow column>
        {(data === undefined && (
          <PageStatus label="Base planogram is loading..." icon="spinner" />
        )) ||
          (data?.status === "IN_PROGRESS" && (
            <PageStatus label="Base planogram is generating..." icon="spinner" />
          )) ||
          (data === null && (
            <PageStatus label="Base planogram not found" color={Color.red} icon="alert" />
          )) ||
          (data?.status === "ERROR" && (
            <PageStatus label={extractBasePogError(data)} color={Color.red} icon="alert" />
          )) ||
          (data?.status === "DONE" && isStoreProductMode && <StoreProductGenerationInfoState />) ||
          (data?.status === "DONE" && !isStoreProductMode && basePog && (
            <>
              <PlanogramToolbar
                pogAtom={merchflows_merchflowId_BasePog_basePogId_PogAtom}
                showCanvasRenderer
              />

              <Planogram
                pogAtom={merchflows_merchflowId_BasePog_basePogId_PogAtom as unknown as PogAtom}
                isEditable
                isPanelVisible
                isPog
                merchflowId={merchflowId}
                currentStoreCode={data.store_code || undefined}
                isWithBorder
                preloadedAvailableStores={availableStores}
                allowKonvaEditor
                basePogId={basePogId}
                onGenerate={openGenerateStorePlanograms}
              />

              <PlanogramLegend />

              <PlanogramHovererDetails />
            </>
          )) ||
          null}
      </Flex>

      <Flex justify="between" align="center">
        {isStoreProductMode ? (
          <Flex />
        ) : (
          <PlanogramMetrics
            pogAtom={merchflows_merchflowId_BasePog_basePogId_PogAtom}
            isLoading={isPlanogramLoading}
          />
        )}

        <Flex gap="10px" align="center">
          {isModified && !isStoreProductMode && (
            <>
              <Button
                color="red"
                variant="inverted"
                isDisabled={isSaveLoading || isSavingDone}
                onClick={() => {
                  revertPog();
                  setGlobalChanges(false);
                }}
              >
                Revert Changes
              </Button>

              <Button
                color="primary"
                isEnabled={isPogAvailable}
                isLoading={isSaveLoading}
                isSuccessful={isSavingDone}
                onClick={() => onClickSave(false)}
                dropdown={[{ label: "Save New Version", onClick: () => onClickSave(true) }]}
              >
                Save
              </Button>
            </>
          )}

          {(!isModified || isStoreProductMode) && (
            <>
              {!isStoreProductMode && (
                <>
                  <Button
                    color="greenSmoke"
                    variant="inverted"
                    dropdown={[
                      { label: "PM data", onClick: () => downloadLogs("pm_data_path") },
                      {
                        label: "Pogger request",
                        onClick: () => downloadLogs("pogger_request_path"),
                      },
                      {
                        label: "Pogger response",
                        onClick: () => downloadLogs("pogger_response_path"),
                      },
                      { label: "Modified PSA", onClick: downloadModifiedPsa },
                    ]}
                    isDisabled={isPlanogramLoading || isGenerateStorePlanogramsLoading}
                    isLoading={hasLoader(RouteGetMerchflowBasePog_basePogId_logs)}
                  >
                    Download Logs
                  </Button>

                  <Button
                    color="greenSmoke"
                    variant="inverted"
                    onClick={openReport}
                    isDisabled={isPlanogramLoading || isGenerateStorePlanogramsLoading}
                    isEnabled={isPogAvailable}
                  >
                    View Report
                  </Button>

                  <Button
                    color="greenSmoke"
                    onClick={onFavorite}
                    isEnabled={isPogAvailable}
                    isDisabled={isPlanogramLoading || isGenerateStorePlanogramsLoading}
                    isLoading={isFavoriteLoading}
                    isSuccessful={isFavoriteDone}
                  >
                    {data?.favorited ? "Unfavorite" : "Favorite"}
                  </Button>
                </>
              )}

              {canCreate && (
                <Button
                  color="primary"
                  onClick={() => void openGenerateStorePlanograms()}
                  isDisabled={isPlanogramLoading || isFavoriteLoading}
                  isEnabled={isGenerateStorePlanogramsEnabled}
                  isLoading={isGenerateStorePlanogramsLoading}
                >
                  Generate Store Planograms
                </Button>
              )}
            </>
          )}
        </Flex>
      </Flex>
    </Page>
  );
};
